import { useCallback } from "react"
import { Table, TableColumn } from "resha"
import { Button } from "dash-styled"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as DragIcon } from "icons/lib/drag.svg"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"

const getKey = () => null

interface ArrayAsTableBaseProps<T> {
    addButtonLabel?: string
    internalColumns: TableColumn<T>[]
    values: T[]
    onChange: (newValue: T[]) => void
    defaultValue: T
}

/**
 * This is a base component to render a sortable array of items as a table.
 * The first column is an icon to sort items followed by "internalColumns" followed by a column with "delete icons"
 * A button to add new items is rendered under the table.
 */
export const ArrayAsTableBase = <T extends any>({
    addButtonLabel,
    internalColumns,
    values,
    onChange,
    defaultValue
}: ArrayAsTableBaseProps<T>) => {
    const columns: TableColumn<T>[] = [
        {
            className: (item, isChecked, index) =>
                "w-1/12 pl-2 bg-white border-b-rhino-300",
            headerClassName: "hidden",
            renderHeader: () => null,
            render: () => {
                return <DragIcon className="w-8 h-8" />
            }
        },
        ...internalColumns,
        {
            headerClassName: "hidden",
            className: (item, isChecked, index) =>
                "w-1/12 bg-white border-b-rhino-300",
            renderHeader: () => null,
            render: (value: T, isChecked: boolean, index: number) => {
                return (
                    <div className="flex items-center justify-end">
                        <div
                            className="p-2 cursor-pointer"
                            onClick={() => {
                                onChange(
                                    values.filter((_, idx) => idx !== index)
                                )
                            }}
                        >
                            <TrashIcon className="w-4 h-4" />
                        </div>
                    </div>
                )
            }
        }
    ]
    const handleDrop = useCallback(
        (items: T[]) => {
            onChange(items)
        },
        [onChange]
    )

    const handleAddHeader = useCallback(() => {
        onChange([...values, defaultValue])
    }, [values, onChange, defaultValue])
    return (
        <div>
            <Table
                className="w-full border-t border-t-rhino-300"
                draggable
                getKey={getKey}
                columns={columns}
                items={values}
                onDrop={handleDrop}
            />
            <Button
                className="mt-4 mb-8 flex items-center"
                size="xs"
                color="violet"
                variant="text"
                onClick={handleAddHeader}
            >
                <AddIcon className="mr-2" />
                {addButtonLabel}
            </Button>
        </div>
    )
}
